/** Centralized API client for backend calls */
export interface ApiClientOptions {
  baseUrl?: string;
  headers?: Record<string, string>;
}

export class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string,string>;

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = opts.baseUrl || process.env.NEXT_PUBLIC_API_BASE_URL || '';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...opts.headers,
    };
  }

  private buildUrl(path: string) {
    if (path.startsWith('http')) return path;
    return `${this.baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  }

  async get<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(this.buildUrl(path), {
      ...init,
      method: 'GET',
      headers: { ...this.defaultHeaders, ...(init?.headers || {}) },
      cache: 'no-store',
    });
    
    if (res.status === 403) {
      if (typeof window !== 'undefined') {
        window.location.href = '/unauthorized';
      }
      throw new Error('Unauthorized access');
    }
    
    if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
    return res.json() as Promise<T>;
  }

  async post<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
    const res = await fetch(this.buildUrl(path), {
      ...init,
      method: 'POST',
      headers: { ...this.defaultHeaders, ...(init?.headers || {}) },
      body: JSON.stringify(body),
    });
    
    if (res.status === 403) {
      if (typeof window !== 'undefined') {
        window.location.href = '/unauthorized';
      }
      throw new Error('Unauthorized access');
    }
    
    if (!res.ok) throw new Error(`POST ${path} failed: ${res.status}`);
    return res.json() as Promise<T>;
  }
}

export const apiClient = new ApiClient();
